<template>
  <ToolPageLayout
    title="What-if情景模拟"
    subtitle="交互式敏感性分析 · 参数滑块 · 预设场景"
  >
    <div v-if="loading" class="sc loading-state">数据加载中...</div>

    <div v-else class="sc">
      <div class="sc-header">
        <div class="sc-title">
          What-if 场景模拟面板
          <span class="tag tag-blue">交互式</span>
          <span class="tag tag-amber">毛利弹性</span>
        </div>
        <div class="sc-meta">
          <span class="meta-text">基准：{{ impact.period_label || '2026年5月' }}实际数据 | 实时计算</span>
          <span v-if="showWarning" class="sc-badge tag-red">⚠ 毛利率低于目标线</span>
        </div>
      </div>

      <div class="whatif-stack">
        <!-- 参数滑块 -->
        <section class="sliders-section">
          <div class="section-title">⚙ 调整关键参数</div>
          <div class="section-sub">拖动滑块或点击预设场景，实时观察毛利率变化</div>

          <div
            v-for="param in parameters"
            :key="param.param_code"
            class="slider-group"
          >
            <div class="slider-label">
              <span class="slider-name">{{ paramMeta[param.param_code].icon }} {{ param.param_name }}</span>
              <span :class="['slider-val', valTagClass(param.param_code)]">
                {{ formatSliderVal(param) }}
              </span>
            </div>
            <div class="slider-desc">{{ param.description }}</div>
            <input
              v-model.number="sliderValues[param.param_code]"
              type="range"
              :min="param.range_min"
              :max="param.range_max"
              :step="param.step"
              @input="activePreset = ''"
            >
            <div class="slider-range">
              <span
                v-for="(label, idx) in paramMeta[param.param_code].rangeLabels"
                :key="idx"
              >{{ label }}</span>
            </div>
          </div>

          <div class="sensitivity-summary" v-html="sensitivityHtml" />
        </section>

        <!-- 模拟结果 -->
        <section class="impact-section">
          <div class="impact-metrics">
            <div class="impact-card primary">
              <div class="impact-card-label">模拟综合毛利率</div>
              <div class="impact-card-val" :style="{ color: gmColor }">{{ fmtGM(computed.grossMargin) }}</div>
              <div class="impact-card-delta" :style="{ color: deltaColor(computed.vsTarget) }">
                vs 目标 {{ fmtGM(impact.target_gm_pct) }} 差距 {{ fmtPP(computed.vsTarget) }}
              </div>
            </div>
            <div class="impact-card">
              <div class="impact-card-label">模拟毛利额（月）</div>
              <div class="impact-card-val" :style="{ color: gpColor }">{{ fmtNum(computed.grossProfit) }}万</div>
              <div class="impact-card-delta" :style="{ color: deltaColor(computed.vsBudgetGP) }">
                vs 预算 {{ fmtWanDiff(computed.vsBudgetGP) }}
              </div>
            </div>
            <div class="impact-card">
              <div class="impact-card-label">模拟经营利润（月）</div>
              <div class="impact-card-val" :style="{ color: opColor }">{{ fmtNum(computed.operatingProfit) }}万</div>
              <div class="impact-card-delta" :style="{ color: deltaColor(computed.vsBudgetOP) }">
                vs 预算 {{ fmtWanDiff(computed.vsBudgetOP) }}
              </div>
            </div>
          </div>
        </section>

        <!-- 预设场景 -->
        <section class="presets-section">
          <div class="section-title">🎨 预设场景对比</div>
          <div class="section-sub">点击卡片一键切换参数，观察不同假设下的毛利率变化</div>
          <div class="scenario-presets">
            <div
              v-for="preset in presets"
              :key="preset.preset_name"
              :class="['scenario-card', { active: activePreset === preset.preset_name }, presetStyle(preset.preset_name).cardClass]"
              @click="applyPreset(preset)"
            >
              <div class="sc-name" :style="{ color: presetStyle(preset.preset_name).nameColor }">
                {{ presetStyle(preset.preset_name).dot }} {{ preset.preset_name }}场景
              </div>
              <div class="sc-gm" :style="{ color: presetGmColor(preset) }">{{ preset.result_gm_pct }}%</div>
              <div class="sc-delta" :style="{ color: presetDeltaColor(preset) }">
                {{ presetDeltaText(preset) }}
              </div>
              <div class="sc-desc" v-html="presetDesc(preset)" />
            </div>
          </div>
        </section>
      </div>
    </div>
  </ToolPageLayout>
</template>

<script>
import ToolPageLayout from './ToolPageLayout.vue'
import { fetchWhatIf, unwrapFpaResponse } from '@/api/fpa'
import { MOCK_WHATIF } from './mockData'
import { fmtWan } from './formatters'

const PARAM_META = {
  commodity_change: {
    icon: '📦',
    rangeLabels: ['-10% (通缩场景)', '0% (持平)', '+5% (当前)', '+20% (严重通胀)']
  },
  price_rate: {
    icon: '💰',
    rangeLabels: ['40% (大部分未兑现)', '60%', '75% (当前)', '100% (完全兑现)']
  },
  cost_down: {
    icon: '✅',
    rangeLabels: ['50% (严重滞后)', '70% (当前)', '100% (达成)', '120% (超预期)']
  },
  capacity: {
    icon: '🏭',
    rangeLabels: ['55% (严重闲置)', '62% (当前)', '75%', '95% (满产)']
  }
}

const PRESET_STYLE = {
  '保守': { dot: '🔴', nameColor: '#e54b3d', cardClass: '' },
  '基准': { dot: '🔵', nameColor: '#4a5ff3', cardClass: '' },
  '激进': { dot: '🟢', nameColor: '#0aa66a', cardClass: '' }
}

export default {
  name: 'ToolFpaWhatIf',
  components: { ToolPageLayout },
  data() {
    return {
      loading: true,
      impact: {},
      parameters: [],
      presets: [],
      sliderValues: {},
      activePreset: '基准',
      paramMeta: PARAM_META,
      modelBase: null
    }
  },
  computed: {
    computed() {
      return this.computeImpact(this.sliderValues)
    },
    showWarning() {
      return this.computed.grossMargin < 26
    },
    gmColor() {
      const gm = this.computed.grossMargin
      const target = this.impact.target_gm_pct || 30.5
      if (gm >= target) return '#0aa66a'
      if (gm >= 26) return '#d98213'
      return '#e54b3d'
    },
    gpColor() {
      return this.computed.grossProfit >= (this.modelBase?.budgetGP || 0) ? '#0aa66a' : '#e54b3d'
    },
    opColor() {
      return this.computed.operatingProfit >= 0 ? '#0aa66a' : '#e54b3d'
    },
    sensitivityHtml() {
      const r = this.computed
      const vals = this.sliderValues
      const items = [
        { code: 'commodity_change', name: '大宗涨跌幅', unit: '±1pp' },
        { code: 'price_rate', name: '内单涨价兑现率', unit: '±1pp' },
        { code: 'capacity', name: '产能利用率', unit: '±1pp' },
        { code: 'cost_down', name: '采购降本完成率', unit: '±1pp' }
      ].map(item => {
        const perturbed = { ...vals, [item.code]: (vals[item.code] || 0) + 1 }
        const next = this.computeImpact(perturbed)
        return { ...item, sens: Math.round(Math.abs(next.grossProfit - r.grossProfit)) }
      }).sort((a, b) => b.sens - a.sens)

      let html = '<strong>⚠ 毛利敏感度排序（当前参数点）：</strong>'
      items.forEach((item, i) => {
        if (i > 0) html += ' > '
        html += `${item.name} (${item.unit}→毛利±${item.sens}万)`
      })
      return html
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    fmtWan,
    async loadData() {
      this.loading = true
      try {
        const res = unwrapFpaResponse(await fetchWhatIf())
        const data = res && res.impact ? res : MOCK_WHATIF
        this.applyData(data)
      } catch (e) {
        this.applyData(MOCK_WHATIF)
      } finally {
        this.loading = false
      }
    },
    applyData(data) {
      this.impact = data.impact || MOCK_WHATIF.impact
      this.parameters = data.parameters || MOCK_WHATIF.parameters
      this.presets = data.presets || MOCK_WHATIF.presets
      this.modelBase = this.buildModelBase(this.impact, this.parameters)
      const values = {}
      this.parameters.forEach(p => { values[p.param_code] = p.baseline })
      this.sliderValues = values
      this.activePreset = '基准'
    },
    buildModelBase(impact, parameters) {
      const getBaseline = (code, fallback) => {
        const p = parameters.find(x => x.param_code === code)
        return p ? p.baseline : fallback
      }
      const revenue = impact.revenue_wan || 46143
      const grossProfit = impact.gross_profit_wan || 11131
      const operatingProfit = impact.operating_profit_wan || -2729
      const cost = revenue - grossProfit
      return {
        revenue,
        cost,
        grossProfit,
        grossMargin: impact.gross_margin_pct || 24.12,
        targetGM: impact.target_gm_pct || 30.5,
        budgetGP: Math.round(revenue * 0.319),
        budgetOP: impact.budget_op_wan != null ? impact.budget_op_wan : 1400,
        materialPct: 0.60,
        laborPct: 0.18,
        overheadPct: 0.22,
        opex: grossProfit - operatingProfit,
        capacityBase: getBaseline('capacity', 62),
        overheadFixedPct: 0.55,
        priceRealizationBase: getBaseline('price_rate', 75),
        pricePoolSize: Math.round(revenue * 0.75),
        priceRate: 0.04,
        materialBase: Math.round(cost * 0.60),
        commodityExposure: 0.80,
        commodityBase: getBaseline('commodity_change', 5),
        costDownBase: getBaseline('cost_down', 70),
        costDownMonthly: 150
      }
    },
    computeImpact(sliderValues) {
      const base = this.modelBase
      if (!base) {
        return {
          grossMargin: 0, grossProfit: 0, operatingProfit: 0,
          vsTarget: 0, vsBudgetGP: 0, vsBudgetOP: 0
        }
      }

      const commodity = sliderValues.commodity_change ?? base.commodityBase
      const priceReal = sliderValues.price_rate ?? base.priceRealizationBase
      const costDown = sliderValues.cost_down ?? base.costDownBase
      const capacity = sliderValues.capacity ?? base.capacityBase

      const commodityDelta = commodity - base.commodityBase
      const materialImpact = -(commodityDelta / 100) * base.materialBase * base.commodityExposure

      const priceRealDelta = priceReal - base.priceRealizationBase
      const priceImpact = (priceRealDelta / 100) * base.pricePoolSize * base.priceRate

      const costDownDelta = costDown - base.costDownBase
      const costDownImpact = (costDownDelta / 100) * base.costDownMonthly

      const capacityDelta = capacity - base.capacityBase
      const overheadFixed = base.cost * base.overheadPct * base.overheadFixedPct
      const capacityImpact = -(capacityDelta / base.capacityBase) * overheadFixed * 0.4

      const totalCostChange = -materialImpact + capacityImpact - costDownImpact
      const totalRevenueChange = priceImpact

      const newRevenue = base.revenue + totalRevenueChange
      const newCost = base.cost + totalCostChange
      const newGP = newRevenue - newCost
      const newGM = newRevenue > 0 ? (newGP / newRevenue) * 100 : 0
      const newOP = newGP - base.opex

      return {
        grossMargin: newGM,
        grossProfit: newGP,
        operatingProfit: newOP,
        vsTarget: newGM - base.targetGM,
        vsBudgetGP: newGP - base.budgetGP,
        vsBudgetOP: newOP - base.budgetOP
      }
    },
    applyPreset(preset) {
      this.activePreset = preset.preset_name
      this.sliderValues = {
        commodity_change: preset.commodity_change,
        price_rate: preset.price_rate,
        cost_down: preset.cost_down,
        capacity: preset.capacity
      }
    },
    formatSliderVal(param) {
      const val = this.sliderValues[param.param_code]
      if (param.param_code === 'commodity_change') {
        return (val >= 0 ? '+' : '') + Number(val).toFixed(1) + param.unit
      }
      return val + param.unit
    },
    valTagClass(code) {
      const val = this.sliderValues[code]
      if (code === 'commodity_change') {
        if (val > 8) return 'tag-red'
        if (val < 0) return 'tag-green'
        return 'tag-amber'
      }
      if (code === 'price_rate') {
        if (val >= 90) return 'tag-green'
        if (val >= 70) return 'tag-blue'
        return 'tag-red'
      }
      if (code === 'cost_down') {
        if (val >= 100) return 'tag-green'
        if (val >= 80) return 'tag-blue'
        return 'tag-red'
      }
      if (code === 'capacity') {
        if (val >= 80) return 'tag-green'
        if (val >= 70) return 'tag-blue'
        return 'tag-red'
      }
      return 'tag-blue'
    },
    presetStyle(name) {
      return PRESET_STYLE[name] || PRESET_STYLE['基准']
    },
    presetGmColor(preset) {
      const gap = preset.result_gm_pct - (this.impact.target_gm_pct || 30.5)
      if (gap >= 0) return '#0aa66a'
      if (gap >= -3) return '#d98213'
      return '#e54b3d'
    },
    presetDeltaColor(preset) {
      const gap = preset.result_gm_pct - (this.impact.target_gm_pct || 30.5)
      if (gap >= 0) return '#0aa66a'
      if (gap >= -3) return '#d98213'
      return '#e54b3d'
    },
    presetDeltaText(preset) {
      const gap = preset.result_gm_pct - (this.impact.target_gm_pct || 30.5)
      const arrow = gap >= 0 ? '▲' : '▼'
      return `${arrow} ${gap >= 0 ? '+' : ''}${gap.toFixed(1)}pp vs 目标`
    },
    presetDesc(preset) {
      const commodity = preset.commodity_change >= 0 ? `大宗继续涨+${preset.commodity_change}%` : `大宗回落至${preset.commodity_change}%`
      return [
        commodity,
        `涨价兑现率${preset.price_rate}%`,
        `降本完成率${preset.cost_down}%`,
        `产能利用率${preset.capacity}%`
      ].join('<br>')
    },
    fmtGM(v) {
      return Number(v).toFixed(2) + '%'
    },
    fmtPP(v) {
      return (v >= 0 ? '+' : '') + Number(v).toFixed(2) + 'pp'
    },
    fmtNum(v) {
      return Math.round(v).toLocaleString('zh-CN')
    },
    fmtWanDiff(v) {
      const sign = v >= 0 ? '+' : ''
      return sign + Math.round(v).toLocaleString('zh-CN') + '万'
    },
    deltaColor(v) {
      return v >= 0 ? '#0aa66a' : '#e54b3d'
    }
  }
}
</script>

<style lang="scss" scoped>
@import './tool-page.scss';

.whatif-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 2px;
}
.section-sub {
  font-size: 9px;
  color: var(--muted);
  margin-bottom: 10px;
}

.sliders-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.slider-group {
  padding: 14px 16px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  background: var(--surface2);
}
.slider-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.slider-name { font-size: 12px; font-weight: 600; color: var(--text); }
.slider-val {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 10px;
}
.slider-desc { font-size: 9px; color: var(--muted); margin-bottom: 8px; }
input[type=range] {
  width: 100%;
  height: 6px;
  appearance: none;
  background: linear-gradient(90deg, var(--blue), var(--purple));
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  &::-webkit-slider-thumb {
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid var(--blue2);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    cursor: pointer;
  }
}
.slider-range {
  display: flex;
  justify-content: space-between;
  font-size: 8px;
  color: var(--muted);
  margin-top: 4px;
  gap: 4px;
  span { flex: 1; text-align: center; &:first-child { text-align: left; } &:last-child { text-align: right; } }
}

.sensitivity-summary {
  padding: 12px 14px;
  background: rgba(217, 130, 19, 0.04);
  border-radius: var(--radius-sm);
  border: 1px solid rgba(217, 130, 19, 0.12);
  font-size: 10px;
  color: var(--text2);
  line-height: 1.6;
  ::v-deep strong { color: var(--amber); }
}

.impact-section { display: flex; flex-direction: column; gap: 8px; }
.impact-metrics { display: flex; flex-direction: column; gap: 6px; }
.impact-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  &.primary {
    background: linear-gradient(135deg, rgba(45, 127, 249, 0.06), rgba(0, 175, 240, 0.03));
    border-color: rgba(45, 127, 249, 0.2);
  }
}
.impact-card-label {
  font-size: 11px;
  color: var(--text2);
  font-weight: 500;
  white-space: nowrap;
  min-width: 120px;
}
.impact-card-val { font-size: 18px; font-weight: 800; flex: 1; }
.impact-card-delta {
  font-size: 10px;
  font-weight: 500;
  margin-left: auto;
  white-space: nowrap;
}

.presets-section { display: flex; flex-direction: column; gap: 8px; }
.scenario-presets {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.scenario-card {
  padding: 14px;
  border-radius: var(--radius-sm);
  border: 2px solid var(--border);
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  background: var(--surface2);
  &:hover {
    border-color: var(--blue);
    box-shadow: 0 2px 8px rgba(45, 127, 249, 0.12);
    transform: translateY(-1px);
  }
  &.active {
    border-color: var(--blue2);
    background: linear-gradient(135deg, rgba(74, 95, 243, 0.06), rgba(45, 127, 249, 0.03));
    box-shadow: 0 4px 12px rgba(45, 127, 249, 0.18);
  }
}
.sc-name { font-size: 13px; font-weight: 700; margin-bottom: 6px; }
.sc-gm { font-size: 20px; font-weight: 800; margin-bottom: 4px; }
.sc-delta { font-size: 11px; font-weight: 600; }
.sc-desc {
  font-size: 9px;
  color: var(--muted);
  margin-top: 6px;
  line-height: 1.4;
}

.sc-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.meta-text { font-size: 10px; color: var(--muted); }

.tag-blue { background: rgba(45, 127, 249, 0.08); color: var(--blue); }
.tag-amber { background: rgba(217, 130, 19, 0.08); color: var(--amber); }
.tag-red { background: rgba(229, 75, 61, 0.08); color: var(--red); }
.tag-green { background: rgba(10, 166, 106, 0.08); color: var(--green); }
.tag-purple { background: rgba(127, 85, 217, 0.08); color: var(--purple); }

.sc-badge.tag-red {
  font-size: 9px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}

.loading-state {
  padding: 40px;
  text-align: center;
  font-size: 12px;
  color: var(--muted);
}

@media (max-width: 900px) {
  .scenario-presets { grid-template-columns: 1fr; }
  .impact-card { flex-wrap: wrap; }
  .impact-card-delta { margin-left: 0; width: 100%; text-align: right; }
}
</style>
